import React from 'react'
import queryString, { ParsedQuery } from 'query-string'

import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material'
import { Box, InputBase, IconButton, alpha } from '@mui/material'
import { useLocation, useHistory } from 'react-router-dom'
import { useHotkeys } from 'react-hotkeys-hook'

import SearchBarResultsMenu from '../SearchBarResultsMenu'
import handleKeyboardArrow from './handleKeyboardArrow'
import { useDebounce } from 'use-debounce'
import isHelpString from '../QueryLanguageHelper/isHelpString'
import isQueryTag from '../QueryLanguageHelper/isQueryTag'
import { secondaryColorBright, secondaryColorBrightOpacity } from '@edge/spark/ui-mui'

const searchOpenPx = '380px'

interface Props {
    mobile?: boolean
    disabled: boolean
    searchText: string
    setSearchText: React.Dispatch<React.SetStateAction<string>>
}

const SEARCH_PATH = '/search'
const SPACE_COLON_SPACE_REGEX = /\s?:\s?/g
const QUESTION_REPLACE_REGEX = /\?(.+)/

const SearchBar: React.FC<Props> = ({ mobile, disabled, searchText, setSearchText }: Props) => {
    const history = useHistory()
    const { pathname, search } = useLocation()
    const [focused, setFocused] = React.useState(false)
    const [menuOpen, setMenuOpen] = React.useState(false)
    const [menuSelection, setMenuSelection] = React.useState<FilterType>('ALL_VEHICLES')
    const [singleResult, setSingleResult] = React.useState<string | null>(null)
    const [shortDebounce] = useDebounce(searchText, 500)

    const params = queryString.parse(search)
    const isHelpTag = isHelpString(searchText) || isQueryTag(searchText)

    const updateSingleResult = (newString: string | null) => setSingleResult(newString)

    const navigate = React.useCallback(() => {
        let newParams: ParsedQuery<string> = {}
        if (pathname === SEARCH_PATH) {
            newParams = params
        } else {
            newParams.filter = menuSelection
        }
        newParams.query = searchText
        const newUrl = `${SEARCH_PATH}?${queryString.stringify(newParams)}`
        history.push(newUrl)
    }, [history, pathname, searchText, menuSelection, params])

    const handleValueChange = React.useCallback(() => {
        if (pathname === SEARCH_PATH && shortDebounce !== params.query && !isHelpTag) {
            navigate()
        }
    }, [pathname, shortDebounce, params.query, isHelpTag, navigate])

    React.useEffect(handleValueChange, [shortDebounce])

    const keyupHandler = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (pathname === SEARCH_PATH) {
            return
        }
        let direction: 'down' | 'up' = 'down'
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (e.key === 'ArrowUp') {
                direction = 'up'
            }
            setMenuSelection(handleKeyboardArrow({ direction, previousSelection: menuSelection }))
        }
        if (e.key === 'Enter') {
            if (singleResult) {
                history.push(singleResult)
            } else {
                navigate()
            }
        }
    }

    const updateValueFromRoute = React.useCallback(() => {
        if (pathname === SEARCH_PATH) {
            const query = typeof params.query === 'string' ? params.query : ''
            setSearchText(query)
            setFocused(true) /// ***** Specifically for search bar ***** ///
        } else {
            setSearchText('')
        }
    }, [params.query, pathname])

    React.useEffect(updateValueFromRoute, [pathname, search])

    React.useEffect(() => {
        // This useEffect handles showing the search results menu dropdown
        if (focused && pathname !== SEARCH_PATH && searchText.length > 0 && !menuOpen) {
            setMenuOpen(true)
        }
    }, [focused, pathname, searchText, menuOpen])

    // Open help dropdown on SEARCH_PATH routes
    React.useEffect(() => {
        if (isHelpTag) {
            setMenuOpen(true)
        }
    }, [isHelpTag])

    React.useEffect(() => {
        // This useEffect handles all the conditions that cause us to want to
        // hide the search results summary dropdown
        if (pathname === SEARCH_PATH && menuOpen && !isHelpTag) {
            setMenuOpen(false)
        }
        if (searchText.length === 0 && menuOpen) {
            setMenuOpen(false)
        }

        // We need a small delay here to allow click events in the dropdown to fire
        if (!focused && menuOpen) {
            setTimeout(() => setMenuOpen(false), 100)
        }
    }, [pathname, menuOpen, focused, searchText, isHelpTag])

    // / ***** Sets search bar closed if path changes with exception of search ***** ///
    React.useEffect(() => {
        history.listen((dif) => {
            if (!dif.pathname.includes('search') || dif.pathname.includes('searchdetails')) {
                setFocused(false)
            }
        })
    }, [history, focused])

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const nextValue = e.currentTarget.value
            .replaceAll(SPACE_COLON_SPACE_REGEX, ':')
            .replace(QUESTION_REPLACE_REGEX, '$1')
        setSearchText(nextValue)
    }

    const globalSearchInputRef = React.useRef<HTMLInputElement>(null)
    useHotkeys('/', () => globalSearchInputRef.current?.focus(), {
        keyup: true,
    })
    return (
        <div onFocus={() => setFocused(true)}>
            <Box
                sx={(theme) => ({
                    border: 'solid .6px #DCDCDC',
                    padding: 0.8,
                    '&:focus-within': {
                        boxShadow: theme.shadows[2],
                        border: 'none',
                        backgroundColor: alpha(theme.palette.common.black, 0.02),
                    },
                    transition: theme.transitions.create('boxShadow', {
                        easing: theme.transitions.easing.easeInOut,
                    }),
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    borderRadius: 50,
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.common.black, 0.02),
                    },
                    marginLeft: { xs: 0, sm: 1 },
                    width: { xs: '100%', sm: 'auto' },
                })}
            >
                <InputBase
                    inputProps={{ spellCheck: 'false' }}
                    inputRef={globalSearchInputRef}
                    placeholder={focused ? 'Search vehicles and more...' : 'Search...'}
                    value={searchText}
                    data-test="Search-Bar"
                    disabled={disabled}
                    sx={[
                        {
                            paddingLeft: 1,
                            maxWidth: searchOpenPx,
                        },
                        focused &&
                            !mobile && {
                                width: searchOpenPx,
                                transition: (theme) =>
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }),
                            },
                        !focused && {
                            transition: (theme) =>
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.leavingScreen,
                                }),
                            overflowX: 'hidden',
                            width: (theme) => ({ xs: theme.spacing(21), sm: theme.spacing(36) }),
                        },
                    ]}
                    onKeyUp={keyupHandler}
                    onChange={handleOnChange}
                    startAdornment={
                        searchText ? (
                            <IconButton color="primary" size="small" onClick={(): void => setSearchText('')}>
                                <CloseIcon />
                            </IconButton>
                        ) : undefined
                    }
                    fullWidth
                />
                <Box
                    sx={{
                        backgroundColor: secondaryColorBright,
                        '&:hover': {
                            backgroundColor: secondaryColorBrightOpacity,
                        },
                        borderTopRightRadius: 50,
                        borderBottomRightRadius: 50,
                        right: 0,
                        height: '100%',
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <IconButton size="large" sx={{ '&:hover': { background: 'transparent' } }} onClick={navigate}>
                        <SearchIcon sx={{ color: (theme) => alpha(theme.palette.common.white, 0.95) }} />
                    </IconButton>
                </Box>
            </Box>
            {menuOpen && (
                <SearchBarResultsMenu
                    mobile={mobile}
                    selectedFilter={menuSelection}
                    searchString={isHelpTag ? searchText : shortDebounce}
                    updateSingleResult={updateSingleResult}
                />
            )}
        </div>
    )
}

export default SearchBar
